import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { CloseOutlined, CheckCircleOutline, ErrorOutline } from '@material-ui/icons';
import { ReactComponent as Warning } from 'assets/general/icons/warning.svg';

import useKycStatusStream from 'hooks/useKycStatusStream';
import { KYCStatus } from 'services/types/kycStatus';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 16,
    width: '100%',
    maxWidth: 440,
  },
  content: {
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
  },
  closeRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  statusRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  iconSuccess: {
    fontSize: 48,
    color: theme.palette.success.main,
  },
  iconError: {
    fontSize: 48,
    color: theme.palette.error.main,
  },
  actions: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '20px',
  },
  actionButton: {
    textTransform: 'none',
  },
}));

const STATUS_TIMEOUT_MS = 8000;

enum VerificationView {
  Pending = 'PENDING',
  Verified = 'VERIFIED',
  Rejected = 'REJECTED',
  Delayed = 'DELAYED',
}

type IdentityVerificationStatusDialogProps = {
  open: boolean;
  onClose: () => void;
  onRetry?: () => void;
  onVerified?: () => void;
};

const IdentityVerificationStatusDialog = ({
  open,
  onClose,
  onRetry,
  onVerified,
}: IdentityVerificationStatusDialogProps) => {
  const classes = useStyles();
  const { data, error, connect, disconnect } = useKycStatusStream();
  const [timedOut, setTimedOut] = useState(false);
  const notifiedRef = useRef(false);
  const isAuthRedirect = error?.status === 307;

  const status = useMemo(() => {
    // SSE payload may include either `verified` or a KYC status string.
    const normalizedStatus =
      typeof data?.status === 'string' ? data.status.toUpperCase() : data?.status;

    if (data?.verified === true || normalizedStatus === KYCStatus.COMPLETED) {
      return VerificationView.Verified;
    }

    if (
      data?.verified === false ||
      normalizedStatus === KYCStatus.FAILED ||
      data?.errorMessage ||
      (error && !isAuthRedirect)
    ) {
      return VerificationView.Rejected;
    }

    if (timedOut) {
      return VerificationView.Delayed;
    }

    return VerificationView.Pending;
  }, [data, error, isAuthRedirect, timedOut]);

  const rejectionMessage =
    data?.errorMessage ||
    (error && !isAuthRedirect ? 'Unable to check verification status. Please try again.' : null) ||
    'Identity verification was not successful. Please try again.';

  const handleClose = useCallback(
    (forceRefresh?: boolean) => {
      onClose();
      disconnect();

      if (
        ((status === VerificationView.Verified && !notifiedRef.current) || forceRefresh) &&
        status !== VerificationView.Delayed
      ) {
        notifiedRef.current = true;
        onVerified?.();
      }
    },
    [disconnect, onClose, onVerified, status]
  );

  const handleTryAgain = () => {
    disconnect();
    onClose();
    onRetry?.();
  };

  useEffect(() => {
    if (!open) return;

    setTimedOut(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (status === VerificationView.Verified && !notifiedRef.current) {
      notifiedRef.current = true;
      onVerified?.();
    }

    if (status === VerificationView.Pending) {
      notifiedRef.current = false;
    }
  }, [onVerified, open, status]);

  useEffect(() => {
    if (!open) {
      disconnect();
    }
  }, [disconnect, open]);

  useEffect(() => {
    if (!open) return;

    connect();
  }, [connect, open]);

  useEffect(() => {
    if (!open || status !== VerificationView.Pending) return;

    // Keep the stream connected after timeout so late status updates can still land.
    const timeoutId = setTimeout(() => {
      setTimedOut(true);
    }, STATUS_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [open, status]);

  useEffect(() => {
    if (!open) return;

    if (status === VerificationView.Verified || status === VerificationView.Rejected) {
      disconnect();
    }
  }, [disconnect, open, status]);

  return (
    <Dialog
      open={open}
      onClose={() => handleClose()}
      maxWidth="md"
      fullWidth
      classes={{ paper: classes.dialogPaper }}>
      <DialogContent className={classes.content}>
        <div className={classes.closeRow}>
          <IconButton aria-label="Close dialog" onClick={() => handleClose(true)}>
            <CloseOutlined />
          </IconButton>
        </div>
        <div className={classes.statusRow}>
          {status === VerificationView.Pending && (
            <>
              <CircularProgress />
              <Typography variant="h5" style={{ fontWeight: 'bold' }}>
                Verification in progress
              </Typography>
              <Typography variant="body1">This usually takes a few seconds</Typography>
            </>
          )}
          {status === VerificationView.Verified && (
            <>
              <CheckCircleOutline className={classes.iconSuccess} />
              <Typography variant="h6">
                Success! Your identity has been verified. You are all set to continue.
              </Typography>
            </>
          )}
          {status === VerificationView.Rejected && (
            <>
              <ErrorOutline className={classes.iconError} />
              <Typography variant="body1">{rejectionMessage}</Typography>
            </>
          )}
          {status === VerificationView.Delayed && (
            <>
              <Warning />
              <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                Verification is taking longer than usual
              </Typography>
              <Typography variant="body1">
                You can close this window and try again later.
              </Typography>
            </>
          )}
        </div>
        {(status === VerificationView.Rejected || status === VerificationView.Delayed) && (
          <div className={classes.actions}>
            {data?.errorMessage ? (
              <Button
                className={classes.actionButton}
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                onClick={handleTryAgain}>
                Try again
              </Button>
            ) : (
              <Button
                className={classes.actionButton}
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                onClick={() => handleClose(true)}>
                Close
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default IdentityVerificationStatusDialog;
